import { FunctionComponent, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUp, faChevronDown, faChevronUp, faPen } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

// The pill is transient (PREC-3850): it appears while the user scrolls and fades
// out again after this idle delay, like an overlay scrollbar.
export const IDLE_HIDE_MS = 1600;
// Scroll events inside this window after mount are the thread's own scroll-to-latest
// (threads open at the bottom), not user scrolling — they must not flash the pill.
export const MOUNT_GRACE_MS = 500;
// Minimum overflow for navigation to be useful at all.
const MIN_OVERFLOW_PX = 40;
// Where a navigated message lands: this many px below the scroll container's top
// edge (a small breathing margin).
const TOP_MARGIN_PX = 12;

/** True when the event target is an editable field, so shortcuts don't hijack typing. */
const isTypingTarget = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}

	const tag = target.tagName;

	return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
};

/**
 * Floating Top / Previous / Next / Reply-draft pill above the reply editor. It
 * appears once the thread is scrolled and drives the shared thread scroll
 * container (`[data-thread-scroll]`) rendered inside {@link useAddInboxChat}.
 * Also binds the `T` / `↑` / `↓` / `D` keyboard shortcuts.
 */
export const ThreadNavToast: FunctionComponent<{
	wrapperRef: RefObject<HTMLElement | null>;
}> = ({ wrapperRef }) => {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	// Distance (px) from the wrapper's bottom to the editor's top, so the pill
	// docks just above the composer instead of floating over the conversation.
	const [bottomPx, setBottomPx] = useState<number | null>(null);

	// Locate the thread's scroll container inside the wrapper. Prefer an explicit
	// marker, then the chat-log's class, then any actually-scrollable descendant —
	// so it keeps working if the class names change.
	const getScrollEl = useCallback((): HTMLElement | null => {
		const wrapper = wrapperRef.current;

		if (!wrapper) {
			return null;
		}

		return (
			wrapper.querySelector<HTMLElement>("[data-thread-scroll]") ??
			wrapper.querySelector<HTMLElement>(".overflow-y-auto.scrollbar-hide") ??
			Array.from(wrapper.querySelectorAll<HTMLElement>("div")).find(
				(el) => el.scrollHeight > el.clientHeight
			) ??
			null
		);
	}, [wrapperRef]);

	const scrollToEl = useCallback((scrollEl: HTMLElement, el: Element) => {
		const offset = el.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top;

		scrollEl.scrollBy({ top: offset - TOP_MARGIN_PX, behavior: "smooth" });
	}, []);

	const navTop = useCallback(() => {
		getScrollEl()?.scrollTo({ top: 0, behavior: "smooth" });
	}, [getScrollEl]);

	// Index of the message currently anchored at the top of the view: the last one
	// whose top is at or above the landing line. Index-based so navigation always
	// advances by exactly one message and never re-selects the just-landed one.
	const currentIndex = useCallback((scrollEl: HTMLElement, messages: Element[]): number => {
		const base = scrollEl.getBoundingClientRect().top;
		let index = 0;

		for (let i = 0; i < messages.length; i++) {
			if (messages[i].getBoundingClientRect().top - base <= TOP_MARGIN_PX + 1) {
				index = i;
			} else {
				break;
			}
		}

		return index;
	}, []);

	const navPrev = useCallback(() => {
		const scrollEl = getScrollEl();

		if (!scrollEl) {
			return;
		}

		const messages = Array.from(scrollEl.querySelectorAll("[data-thread-msg]"));
		const index = currentIndex(scrollEl, messages);

		if (index - 1 >= 0) {
			scrollToEl(scrollEl, messages[index - 1]);
		} else {
			navTop();
		}
	}, [getScrollEl, scrollToEl, navTop, currentIndex]);

	const navNext = useCallback(() => {
		const scrollEl = getScrollEl();

		if (!scrollEl) {
			return;
		}

		const messages = Array.from(scrollEl.querySelectorAll("[data-thread-msg]"));
		const index = currentIndex(scrollEl, messages);

		if (index + 1 < messages.length) {
			scrollToEl(scrollEl, messages[index + 1]);
		} else {
			// Past the last message → jump to the bottom (draft/editor area).
			scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: "smooth" });
		}
	}, [getScrollEl, scrollToEl, currentIndex]);

	const navDraft = useCallback(() => {
		const scrollEl = getScrollEl();

		if (!scrollEl) {
			return;
		}

		const draft =
			scrollEl.querySelector('[data-tour="draft-reply-card"]') ??
			scrollEl.querySelector("[data-draft-card]");

		if (draft) {
			scrollToEl(scrollEl, draft);
		} else {
			scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: "smooth" });
		}
	}, [getScrollEl, scrollToEl]);

	// Transient visibility (PREC-3850): show while the user scrolls, hide after an
	// idle delay. Hovering the pill pauses the hide so it doesn't vanish mid-aim.
	const hideTimerRef = useRef<number | null>(null);
	const hoveredRef = useRef(false);

	const scheduleHide = useCallback(() => {
		if (hideTimerRef.current !== null) {
			window.clearTimeout(hideTimerRef.current);
		}

		hideTimerRef.current = window.setTimeout(() => {
			if (!hoveredRef.current) {
				setVisible(false);
			}
		}, IDLE_HIDE_MS);
	}, []);

	useEffect(() => {
		let raf = 0;
		let detach: (() => void) | undefined;
		const mountedAt = Date.now();

		const attach = () => {
			const scrollEl = getScrollEl();

			if (!scrollEl) {
				raf = requestAnimationFrame(attach);

				return;
			}

			const onScroll = () => {
				if (Date.now() - mountedAt < MOUNT_GRACE_MS) {
					return;
				}

				const overflowing = scrollEl.scrollHeight - scrollEl.clientHeight > MIN_OVERFLOW_PX;

				if (!overflowing) {
					return;
				}

				setVisible(true);
				scheduleHide();
			};

			scrollEl.addEventListener("scroll", onScroll, { passive: true });
			detach = () => scrollEl.removeEventListener("scroll", onScroll);
		};

		attach();

		return () => {
			cancelAnimationFrame(raf);
			detach?.();

			if (hideTimerRef.current !== null) {
				window.clearTimeout(hideTimerRef.current);
			}
		};
	}, [getScrollEl, scheduleHide]);

	// Keep the pill docked to the top edge of the reply editor (which is a sibling
	// of the scroll container, rendered by useAddInboxChat), re-measuring when the
	// editor resizes (collapse/expand) or the layout changes.
	useEffect(() => {
		const wrapper = wrapperRef.current;

		if (!wrapper) {
			return;
		}

		const measure = () => {
			const editor = wrapper.querySelector<HTMLElement>('[data-tour="draft-reply-editor"]');

			if (!editor) {
				setBottomPx(null);

				return;
			}

			const gap = 8;

			setBottomPx(
				Math.max(
					gap,
					wrapper.getBoundingClientRect().bottom - editor.getBoundingClientRect().top + gap
				)
			);
		};

		measure();

		const observer = new ResizeObserver(measure);

		observer.observe(wrapper);

		const editor = wrapper.querySelector<HTMLElement>('[data-tour="draft-reply-editor"]');

		if (editor) {
			observer.observe(editor);
		}

		window.addEventListener("resize", measure);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [wrapperRef]);

	// Keyboard shortcuts.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (isTypingTarget(e.target)) {
				return;
			}

			switch (e.key) {
				case "t":
				case "T":
					navTop();
					break;
				case "ArrowUp":
					e.preventDefault();
					navPrev();
					break;
				case "ArrowDown":
					e.preventDefault();
					navNext();
					break;
				case "d":
				case "D":
					navDraft();
					break;
				default:
					break;
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [navTop, navPrev, navNext, navDraft]);

	const buttonClass =
		"inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-xs font-semibold text-text-meta hover:bg-sidebar-item-hover hover:text-brand-light cursor-pointer";
	const kbdClass =
		"rounded border border-border-light bg-brand-white px-1.5 text-[10px] font-bold text-text-meta";

	return (
		<div
			className={classNames(
				"absolute left-1/2 -translate-x-1/2 z-20 transition-opacity duration-200",
				bottomPx === null && "bottom-4",
				visible ? "opacity-100" : "pointer-events-none opacity-0"
			)}
			style={bottomPx === null ? undefined : { bottom: bottomPx }}
			data-testid="thread-nav-toast"
			onMouseEnter={() => {
				hoveredRef.current = true;

				if (hideTimerRef.current !== null) {
					window.clearTimeout(hideTimerRef.current);
				}
			}}
			onMouseLeave={() => {
				hoveredRef.current = false;
				scheduleHide();
			}}
		>
			{/* One slim row, always: nowrap so the pill never folds onto a second line. */}
			<div className="inline-flex flex-nowrap items-center gap-1 whitespace-nowrap rounded-2xl border border-border-light bg-brand-white/95 p-1 shadow-lg backdrop-blur">
				<button className={buttonClass} type="button" onClick={navTop}>
					<Icon className="w-3.5 h-3.5" icon={faArrowUp} />
					{t("inbox.thread.nav.top")}
					<kbd className={kbdClass}>T</kbd>
				</button>
				<span className="w-px h-4 bg-border-light mx-0.5" />
				<button className={buttonClass} type="button" onClick={navPrev}>
					<Icon className="w-3.5 h-3.5" icon={faChevronUp} />
					{t("inbox.thread.nav.previous")}
					<kbd className={kbdClass}>↑</kbd>
				</button>
				<button className={buttonClass} type="button" onClick={navNext}>
					<Icon className="w-3.5 h-3.5" icon={faChevronDown} />
					{t("inbox.thread.nav.next")}
					<kbd className={kbdClass}>↓</kbd>
				</button>
				<span className="w-px h-4 bg-border-light mx-0.5" />
				<button className={buttonClass} type="button" onClick={navDraft}>
					<Icon className="w-3.5 h-3.5" icon={faPen} />
					{t("inbox.thread.nav.replyDraft")}
					<kbd className={kbdClass}>D</kbd>
				</button>
			</div>
		</div>
	);
};
